import React, { useState, useEffect, useRef, FC, ReactElement } from 'react';
import classNames from 'classnames';

import css from './style.module.scss';

interface IProps<T> {
  children?: ReactElement;
  options: T[];
  trackBy: string;
  label: string;
  onToggleList?: (value: boolean) => void;
  onSelect: (option: T) => void;
  value: T | null;
  width?: '24' | '32' | 'inherit';
  listWidth?: '204' | 'full';
  placement?: 'right';
  optionTextSize?: 'md';
  render?: (isOpened: boolean) => ReactElement;
  OptionsComponent: FC,
  controlClassName?: string,
}

const Dropdown = <T,>({
  children,
  label,
  trackBy,
  options,
  onToggleList = () => {},
  onSelect = () => {},
  value,
  width,
  listWidth,
  placement,
  optionTextSize,
  render,
  controlClassName,
  OptionsComponent
}: IProps<T>) => {
  const [isListOpened, setIsListOpened] = useState(false);
  const dropdownRef = useRef(null);

  const setToggleDropdown = (value: boolean) => {
    setIsListOpened(value);
    onToggleList(value);
  };

  const toggleDropdown = () => {
    setToggleDropdown(!isListOpened);
  };

  const handleOptionClick = (option: T) => {
    setToggleDropdown(false);
    onSelect(option);
  };

  const renderOptions = () => {
    return React.cloneElement(<OptionsComponent />, {
      value,
      label,
      trackBy,
      optionTextSize,
      list: options,
      onClick: handleOptionClick,
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      //@ts-ignore
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setToggleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div
      ref={dropdownRef}
      className={classNames(css.dropdown, { [css[`dropdown_width_${width}`]]: width })}
    >
      <div onClick={toggleDropdown} className={classNames(css.dropdown__control, controlClassName)}>
        {render ? render(isListOpened) : children}
      </div>
      {isListOpened && (
        <div
          className={classNames(css.dropdown__list, {
            [css[`dropdown__list_width_${listWidth}`]]: listWidth,
            [css[`dropdown__list_placement_${placement}`]]: placement,
          })}
        >
          {renderOptions()}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
